/**
 * Heuristic failure risk per machine — the `predict_risk` scoring as a pure,
 * reusable function.
 *
 * ## Provenance
 *
 * This is the scoring inside the assistant's `predict_risk` tool
 * (`shared/assistant/tools.ts`, the demo's line 4014), lifted out unchanged so
 * the driver tree and the routing engine can rank machines the same way the
 * assistant does. Same factors, same weights, same order, same rounding:
 *
 * | Factor | Points |
 * |---|---|
 * | worst component < 20 / < 40 / < 60 | +0.55 / +0.45 / +0.25 |
 * | daily-metric drift > 0.4 %/day (least-squares slope ÷ first value) | +0.20 |
 * | each breakdown work order since 1 Jun 2026 | +0.10 |
 * | each late PM, at most three | +0.05 |
 * | more than 8 abuse events in the window | +0.10 |
 * | worst component > 4,000 h since change | +0.10 |
 *
 * Capped at 0.98 and rounded to two decimals. The action thresholds read the
 * **unrounded, uncapped** sum, exactly as the tool does: > 0.6 plan a
 * change-out this week, > 0.35 watch.
 *
 * **Integration note.** `tools.ts` still carries its own copy (that folder is
 * owned by the assistant lane). At integration `predict_risk` switches to call
 * {@link riskFor}; until then `scripts/oem/check-reliability.mjs` imports both
 * and fails the build if they disagree on any machine.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions over
 * fetched data. No React, no clock, no randomness.
 */

import { BY_ID, FLEET, type ComponentHealth } from '../../data/core.ts';
import type { MineCode, ModelCode } from '../../schema/machine.ts';
import type { DailyMetricRow, ShiftDailyRow } from '../../schema/telemetry.ts';
import type { WorkOrder } from '../../schema/work-order.ts';
import { healthOf, slope, wosOf } from '../oem-engine.ts';
import { round } from './hash.ts';
import { loadReliabilityInputs } from './inputs.ts';

/** The lazy data the score reads. */
export interface RiskCtx {
  readonly workOrders: readonly WorkOrder[];
  readonly dailyMetric: readonly DailyMetricRow[];
  readonly shiftDaily: readonly ShiftDailyRow[];
}

/** The two action thresholds on the unrounded score. */
export const RISK_THRESHOLDS = { plan: 0.6, watch: 0.35 } as const;

/** Breakdowns on or after this date count toward the score. */
export const RISK_BREAKDOWN_SINCE = '2026-06-01';

/** Which rule a factor came from. */
export type RiskFactorKind = 'condition' | 'drift' | 'breakdowns' | 'late-pm' | 'abuse' | 'hours';

/** One contributing factor, with its points. */
export interface RiskFactor {
  kind: RiskFactorKind;
  points: number;
  /** The tool's wording, verbatim. */
  text: string;
}

/** The score for one machine. */
export interface RiskResult {
  machine: string;
  mine: MineCode;
  model: ModelCode;
  /** 0–0.98, two decimals — what `predict_risk` returns as `risk`. */
  risk: number;
  /** The unrounded, uncapped sum the action thresholds read. */
  rawRisk: number;
  /** The tool's `factors` strings, in its order. */
  factors: string[];
  factorDetail: RiskFactor[];
  /** The tool's `recommendation`, verbatim. */
  recommendation: string;
  action: 'plan' | 'watch' | 'none';
  /** The worst component the condition factor read. */
  worstComponent: string | null;
  worstScore: number | null;
  /** Daily-metric drift, %/day. */
  driftPctPerDay: number;
  breakdownsSinceJune: number;
  latePms: number;
  abuse: number;
}

/** Score one machine. */
export function riskFor(machineId: string, ctx: RiskCtx): RiskResult {
  const f = BY_ID[machineId];
  const h = healthOf(machineId);
  const worst: ComponentHealth | undefined = h.slice().sort((a, b) => a.health_score - b.health_score)[0];
  const dm = ctx.dailyMetric.filter((d) => d.machine_id === machineId).map((d) => d.value);
  const sl = (slope(dm) / Math.max(1, Math.abs(dm[0] ?? 0))) * 100;
  const ws = wosOf(machineId, { workOrders: ctx.workOrders });
  const bd90 = ws.filter((w) => w.wo_type === 'Breakdown' && w.created >= RISK_BREAKDOWN_SINCE).length;
  const late = ws.filter((w) => w.status.includes('late')).length;
  const abuse = ctx.shiftDaily.filter((s) => s.machine_id === machineId).reduce((a, s) => a + s.abuse, 0);

  let r = 0;
  const detail: RiskFactor[] = [];
  const add = (kind: RiskFactorKind, points: number, text: string) => {
    r += points;
    detail.push({ kind, points, text });
  };

  if (worst) {
    if (worst.health_score < 20) add('condition', 0.55, `${worst.component} condition ${worst.health_score} (major issue)`);
    else if (worst.health_score < 40) add('condition', 0.45, `${worst.component} condition ${worst.health_score} (very poor)`);
    else if (worst.health_score < 60) add('condition', 0.25, `${worst.component} condition ${worst.health_score} (poor)`);
  }
  if (Math.abs(sl) > 0.4) add('drift', 0.2, `telemetry drifting ${sl > 0 ? '+' : ''}${sl.toFixed(1)}%/day`);
  if (bd90) add('breakdowns', 0.1 * bd90, `${bd90} breakdown(s) since Jun`);
  if (late) add('late-pm', 0.05 * Math.min(3, late), `${late} late PMs`);
  if (abuse > 8) add('abuse', 0.1, `${abuse} abuse events / 12 d`);
  // The tool's second clause compares the stored band to the demo's `'ok'`
  // sentinel, which this data never carries — always true. Kept.
  if (worst && worst.hours_since_change > 4000) add('hours', 0.1, `${worst.hours_since_change} h since change`);

  const action = r > RISK_THRESHOLDS.plan ? 'plan' : r > RISK_THRESHOLDS.watch ? 'watch' : 'none';
  return {
    machine: machineId,
    mine: f.mine,
    model: f.model,
    risk: round(Math.min(0.98, r), 2),
    rawRisk: r,
    factors: detail.map((d) => d.text),
    factorDetail: detail,
    recommendation:
      action === 'plan'
        ? 'Plan change-out / inspection this week'
        : action === 'watch'
          ? 'Watch — add to next PM'
          : 'No action',
    action,
    worstComponent: worst?.component ?? null,
    worstScore: worst?.health_score ?? null,
    driftPctPerDay: sl,
    breakdownsSinceJune: bd90,
    latePms: late,
    abuse,
  };
}

/** Which machines to rank. */
export interface RiskFilter {
  readonly machine?: string | null;
  readonly mine?: MineCode | string | null;
  /** Keep the top `limit`. `predict_risk` keeps 12; omit for all. */
  readonly limit?: number;
}

/**
 * Score and rank, highest risk first. Ties keep fleet order — the same stable
 * sort the tool uses.
 */
export function riskRanking(filter: RiskFilter, ctx: RiskCtx): RiskResult[] {
  let fs = FLEET.slice();
  if (filter.machine) fs = fs.filter((f) => f.machine_id === String(filter.machine).toUpperCase().trim());
  if (filter.mine) fs = fs.filter((f) => f.mine === String(filter.mine).toUpperCase().trim());
  const ranked = fs.map((f) => riskFor(f.machine_id, ctx)).sort((a, b) => b.risk - a.risk);
  return filter.limit === undefined ? ranked : ranked.slice(0, filter.limit);
}

/** Fetch the domains and rank. */
export async function loadRiskRanking(filter: RiskFilter = {}): Promise<RiskResult[]> {
  return riskRanking(filter, await loadReliabilityInputs());
}
